import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["handleClick", "disableBackdropClick", "children", "top", "layout", "narrow"],
      _excluded2 = ["title", "handleClick", "disableBackdropClick", "minHeight", "onPrint", "children", "layout", "timer", "tasks", "save"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import styled from 'styled-components';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DialogContent, Dialog } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { isSmallTouchDevice, safariDetect } from '@mummssoftware/utils/web';
import { SectionHeader } from './SectionHeader'; // @note: https://stackoverflow.com/a/54812656

const HeaderDiv = styled.div.withConfig({
  displayName: "TaskDialog__HeaderDiv",
  componentId: "sc-323jj2-0"
})(["display:inline-flex;align-content:center;align-items:center;gap:5px;"]);
const StyledDialog = styled(Dialog).withConfig({
  displayName: "TaskDialog__StyledDialog",
  componentId: "sc-323jj2-1"
})(["&&{pointer-events:none;}& .resizable{position:relative;border:3px solid ", ";border-top:none;overflow:visible;}& .resizable > *{overflow:visible;}& .resizable > .react-resizable-handle{position:absolute;width:20px;height:20px;bottom:0;right:0;background:url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+');background-position:bottom right;padding:0 3px 3px 0;background-repeat:no-repeat;background-origin:content-box;box-sizing:border-box;cursor:se-resize;}& .MuiDialog-paper{overflow:visible;}"], props => props.theme.palette.error.light);
const StyledPaper = styled(Paper).withConfig({
  displayName: "TaskDialog__StyledPaper",
  componentId: "sc-323jj2-2"
})(["pointer-events:auto;& .MuiDialog-paper{overflow:visible;}"]);
const StyledDiv = styled(Paper).withConfig({
  displayName: "TaskDialog__StyledDiv",
  componentId: "sc-323jj2-3"
})([""]); // size of header (31px) — content scrolls vertically; grid area scrolls horizontally in dashboard

const TaskDialogContent = styled(DialogContent).withConfig({
  displayName: "TaskDialog__TaskDialogContent",
  componentId: "sc-323jj2-4"
})(["flex:1 1 auto;min-height:0;width:100%;min-width:0;max-width:100%;box-sizing:border-box;overflow:auto !important;-webkit-overflow-scrolling:touch;max-height:", "px;"], props => props.x);
let widgetContainerRef; // reference used in component context

function PaperComponent(props) {
  widgetContainerRef = React.useRef(null);
  const positionObj = // position at top to get around display issue in pim on iPad.
  // otherwise if not ipad, display in bottom right.
  isSmallTouchDevice() && safariDetect() ? {
    position: 'fixed',
    right: 0,
    top: 0
  } : {}; // bottom:0; right:0;

  return /*#__PURE__*/React.createElement("div", {
    style: positionObj,
    ref: widgetContainerRef
  }, /*#__PURE__*/React.createElement(StyledPaper, props));
}

const DialogHeader = ({
  handleClick = () => null,
  style,
  id,
  title,
  timer,
  tasks,
  save
}) => /*#__PURE__*/React.createElement(SectionHeader, {
  title: title,
  style: style,
  id: id,
  nav: /*#__PURE__*/React.createElement(HeaderDiv, null, save, tasks, timer)
});
/* disableEnforceFocus : the modal will not prevent focus from leaving the modal while open.
Generally this should never be set to true as it makes the modal less accessible to assistive technologies, like screen readers. 
We need it for cert-app and clearscripts signed verbal tab
*/
// TODO Invalid prop children of type array supplied to resizableBox
// top to have the dialog box open on top of page instead of middle default


function getInnerFlexStyle(layout) {
  if (layout === 'minimize') {
    return undefined;
  }

  return {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    overflow: 'hidden'
  };
}

function getResizeBoxStyle(layout, narrow, innerFlex) {
  if (layout === 'minimize') {
    return {
      overflow: 'hidden',
      maxHeight: 0.7 * window.innerHeight
    };
  }

  if (narrow && innerFlex) {
    return _objectSpread(_objectSpread({}, innerFlex), {}, {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      maxHeight: '100%'
    });
  }

  if (innerFlex) {
    return _objectSpread(_objectSpread({}, innerFlex), {}, {
      maxHeight: 0.9 * window.innerHeight,
      minWidth: Math.min(0.9 * window.innerWidth, 770),
      maxWidth: Math.min(window.innerWidth - 16, 770)
    });
  }

  return {};
}

function getTaskDialogContentMaxHeightPx(narrow) {
  if (typeof window === 'undefined') {
    return 600;
  }

  if (narrow) {
    return window.innerHeight - 31;
  }

  return 0.9 * window.innerHeight - 31;
}

const DialogWrap = _ref => {
  let {
    handleClick,
    disableBackdropClick,
    children,
    top,
    layout,
    narrow
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  const boxProps = {
    id: 'resize-box'
  };
  const innerFlex = getInnerFlexStyle(layout);
  return /*#__PURE__*/React.createElement(StyledDialog, _extends({
    id: "dialog",
    PaperComponent: PaperComponent,
    maxWidth: false,
    "aria-labelledby": "task-title",
    disableEnforceFocus: true // Let the user focus on elements outside the dialog

  }, props, {
    fullScreen: narrow
  }), /*#__PURE__*/React.createElement(StyledDiv, _extends({
    style: getResizeBoxStyle(layout, narrow, innerFlex)
  }, boxProps), /*#__PURE__*/React.createElement("div", {
    style: layout === 'minimize' ? undefined : {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      flex: 1,
      overflow: 'hidden',
      width: '100%',
      minWidth: 0
    }
  }, children)));
};

export const TaskDialogBox = _ref2 => {
  let {
    title,
    handleClick,
    disableBackdropClick,
    minHeight,
    onPrint,
    children,
    layout,
    timer,
    tasks,
    save
  } = _ref2,
      props = _objectWithoutProperties(_ref2, _excluded2);

  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true
  });
  const dragStyle = {
    backgroundColor: theme.palette.error.light,
    overflow: 'visible'
  };
  const x = getTaskDialogContentMaxHeightPx(!!narrow);
  return /*#__PURE__*/React.createElement(DialogWrap, _extends({
    id: "dialog",
    disableBackdropClick: disableBackdropClick,
    handleClick: handleClick,
    layout: layout,
    narrow: narrow
  }, props), /*#__PURE__*/React.createElement(DialogHeader, {
    id: "dialog-title",
    title: layout !== 'minimize' ? title : '',
    style: dragStyle,
    handleClick: handleClick,
    onPrint: onPrint,
    timer: timer,
    tasks: tasks,
    save: save
  }), /*#__PURE__*/React.createElement(TaskDialogContent, {
    x: x
  }, children));
};
export default TaskDialogBox;